import styled from "@emotion/styled";
import { black, darkGrey3, lightGreen, lightGreyTransparent } from "@stepfinance/react";
import type { PopoverProps } from "antd";
import { Button, Popover, Tabs } from "antd";
import type { FC } from "react";
import React, { useState } from "react";
import { useLocation } from "react-router-dom";

import { ReactComponent as LiquidityIcon } from "../../assets/liquidity.svg";
import { ReactComponent as SwapIcon } from "../../assets/transaction-history/swap.svg";
import { useLanguageContext } from "../../contexts/language/language";
import { useNavigationContext } from "../../contexts/navigation";
import { headerButtonStyles } from "../AppBar/RefreshButton";
import { SettingsIcon } from "../Icons";
import type { LiquidityAction } from "../Liquidity/Liquidity";
import { Liquidity } from "../Liquidity/Liquidity";
import { Settings } from "../Slippage/settings";
import { TradeEntry } from "../Trade";

const { TabPane } = Tabs;

const settingsButtonProps: PopoverProps = {
  align: {
    points: ["tr", "br"],
    offset: [0, 12],
  },
  destroyTooltipOnHide: true,
  mouseLeaveDelay: 0.5,
  placement: "bottomRight" as const,
  trigger: ["click"],
};
const iconProps = {
  height: 16,
  width: 16,
};

export const SwapTabs: FC = () => {
  const {
    languageSetting,
    currentLanguageData: { common },
  } = useLanguageContext();

  const location = useLocation();
  const { navigate } = useNavigationContext();
  const { pathname: swapTabPath } = location;
  const activeTab = swapTabPath.startsWith(`/${languageSetting}/liquidity`) ? "liquidity" : "swap";
  const [slippageVisible, setSlippageVisible] = useState(false);

  const handleTabChange = (key: string) => {
    if (activeTab !== key) {
      if (key === "swap") {
        navigate(`/swap`);
      } else {
        navigate(`/liquidity/add`);
      }
    }
  };

  const swapTabsExtraContent = (
    <SwapTabsExtraContentContainer>
      <HeaderButton
        {...settingsButtonProps}
        placement="bottomRight"
        content={<Settings />}
        trigger="click"
        overlayInnerStyle={{
          border: `solid 1px ${lightGreyTransparent}`,
          borderRadius: "8px",
          background: black,
        }}
        open={slippageVisible}
        onOpenChange={setSlippageVisible}
      >
        <SlippageSettingsButton type="default" icon={<SettingsIcon {...iconProps} />} />
      </HeaderButton>
    </SwapTabsExtraContentContainer>
  );

  const swapTabPathSplit = swapTabPath.split("/");
  const liquidityAction = swapTabPathSplit[swapTabPathSplit.length - 1];

  return (
    <>
      <BaseSwapTabs
        type="card"
        activeKey={activeTab}
        onChange={(key) => {
          handleTabChange(key);
        }}
        tabBarExtraContent={swapTabsExtraContent}
      >
        <TabPane
          tab={
            <TabDisplay sidePadding={35} tabSelected={swapTabPath === `/${languageSetting}/swap`}>
              <SwapIcon />
              <span>{common.swap}</span>
            </TabDisplay>
          }
          key="swap"
        >
          <TradeEntry />
        </TabPane>
        <TabPane
          tab={
            <TabDisplay sidePadding={24} tabSelected={swapTabPath.startsWith(`/${languageSetting}/liquidity`)}>
              <LiquidityIcon />
              <span>{common.liquidity}</span>
            </TabDisplay>
          }
          key="liquidity"
        >
          <Liquidity action={liquidityAction as LiquidityAction} />
        </TabPane>
      </BaseSwapTabs>
    </>
  );
};

const SwapTabsExtraContentContainer = styled.div`
  display: flex;
`;

const HeaderButton = styled(Popover)<PopoverProps>`
  ${headerButtonStyles}
  background: ${darkGrey3};
  border: 1px solid ${darkGrey3};

  &:hover {
    -webkit-appearance: none;
    background: ${darkGrey3};
  }
`;

const SlippageSettingsButton = styled(Button)`
  width: 35px !important;
  height: 35px !important;
  background: ${darkGrey3};
  border-radius: 2px;
`;

const BaseSwapTabs = styled(Tabs)`
  margin: auto;
  max-width: 600px;
  .ant-tabs-nav {
    ::before {
      border: none;
    }
    margin: 0;
  }

  .ant-tabs-tab {
    color: #787878;
    background-color: #0a0a0a !important;
    margin: 0 !important;
    border: none !important;
    height: 45px;
    padding: 0 !important;
    border-radius: 8px 8px 0px 0px !important;
    div > :active:hover {
      color: ${lightGreen};
    }
  }

  .ant-tabs-tab-active {
    color: ${lightGreen};
    background-color: #141414 !important;
  }
`;

const TabDisplay = styled.div<{ sidePadding: number; tabSelected: boolean }>`
  display: flex;
  align-items: center;
  padding: 22px ${({ sidePadding }) => `${sidePadding}px`};
  transition: 0.3s;
  svg {
    width: 15px;
    height: 15px;
    margin-right: 10px;
    path {
      transition: 0.3s;
      fill: ${({ tabSelected }) => (tabSelected ? "#06D6A0" : "#787878")};
    }
  }
  &:hover,
  &:focus,
  &:active {
    transition: 0.3s;
    color: ${lightGreen} !important;
    path {
      transition: 0.3s;
      fill: ${lightGreen};
    }
  }
  span {
    font-weight: 800;
  }
`;
